import { format, formatDistanceToNow, parseISO } from 'date-fns';
import React from 'react';
import { HeadType, RowType } from '@atlaskit/dynamic-table/dist/types/types';
import { ApprovalStatus as ApprovalStatusType, Task } from '@css-xp/apd-common';
import { FormattedDate } from '@css-xp/base';
import { ApprovalStatus, withTooltip } from '../../common';
import { statusToShow } from '../../common/status/status';
import { DisplayRequestType } from '../../constants/approvals';
import { DATE_FORMAT, TABLE_COLUMN_KEYS, TABLE_HEADER } from '../../constants/table';
import { getDifferenceOfDatesInDuration, getEntityTypeUrl } from '../../utils';
import { TaskDetailAction } from '../actions';
import './closed-tasks-list.scss';

interface CreateTableRowsParam {
  taskslist: Task[];
}

const getApprovalStatusContent = (status: ApprovalStatusType | undefined | null) => {
  if (!status) {
    return <></>;
  }
  return withTooltip(
    statusToShow[status] ? statusToShow[status].toUpperCase() : status,
    <ApprovalStatus status={status} />
  );
};

export const createTableHead = (withWidth: boolean): HeadType => {
  return {
    cells: [
      {
        key: TABLE_COLUMN_KEYS.CREATED_DATE,
        content: TABLE_HEADER.REQUEST_DATE,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.CREATED_DATE,
      },
      {
        key: TABLE_COLUMN_KEYS.TASK_NUMBER,
        content: TABLE_HEADER.TASK_DISPLAY_ID,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.TASK_NUMBER,
      },
      {
        key: TABLE_COLUMN_KEYS.BUSINESS_KEY,
        content: TABLE_HEADER.ENTITY_Id,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.BUSINESS_KEY,
      },
      {
        key: TABLE_COLUMN_KEYS.REQUEST_TYPE,
        content: TABLE_HEADER.REQUEST_TYPE,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.REQUEST_TYPE,
      },
      {
        key: TABLE_COLUMN_KEYS.INITIATOR,
        content: TABLE_HEADER.INITIATOR,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.INITIATOR,
      },
      {
        key: TABLE_COLUMN_KEYS.APPROVER,
        content: TABLE_HEADER.APPROVER,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.APPROVER,
      },
      {
        key: TABLE_COLUMN_KEYS.STATUS,
        content: TABLE_HEADER.STATUS,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.STATUS,
      },
      {
        key: TABLE_COLUMN_KEYS.COMPLETED,
        content: TABLE_HEADER.COMPLETED,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.COMPLETED,
      },
      {
        key: TABLE_COLUMN_KEYS.COMPLETION_TIME,
        content: TABLE_HEADER.COMPLETION_TIME,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.COMPLETION_TIME,
      },
    ],
  };
};

export const createTableRows = ({ taskslist }: CreateTableRowsParam): Array<RowType> => {
  return taskslist.map((task: Task, index: number) => {
    const createdDate = task.createdDate ? parseISO(task.createdDate) : null;
    const formattedCreatedDate = createdDate ? formatDistanceToNow(createdDate, { addSuffix: true }) : '';
    const completedDate = task.endDate ? parseISO(task.endDate) : null;
    const formattedCompletedDate = completedDate ? format(completedDate, DATE_FORMAT) : '';
    const initiator = task?.initiatorName ?? '';
    const displayRequestType = (task?.requestType && DisplayRequestType[task.requestType]) ?? '';
    const taskDisplayId = task?.taskNumber ?? '';
    const approver = task.ownerName || task.ownerId || task.groupId || '';
    const completionTime =
      createdDate && completedDate ? getDifferenceOfDatesInDuration(createdDate, completedDate) : '';
    return {
      key: `row-${index}`,
      cells: [
        {
          key: createdDate ? createdDate.valueOf() : 0,
          content: createdDate
            ? withTooltip(
                <FormattedDate date={task.createdDate} showTime />,
                <div className="text-overflow">{formattedCreatedDate}</div>
              )
            : '',
          data: formattedCreatedDate,
        },
        {
          key: TABLE_COLUMN_KEYS.TASK_NUMBER + index,
          content: withTooltip(taskDisplayId, <TaskDetailAction task={task} />),
          data: taskDisplayId,
        },
        {
          key: TABLE_COLUMN_KEYS.BUSINESS_KEY + index,
          content: withTooltip(
            task.businessKey,
            <a href={getEntityTypeUrl(task?.entityType, task?.businessKey, task?.entityId)}>
              <div className="text-overflow">{task.businessKey}</div>
            </a>
          ),
          data: task?.businessKey,
        },
        {
          key: TABLE_COLUMN_KEYS.REQUEST_TYPE + index,
          content: withTooltip(displayRequestType, <div className="text-overflow">{displayRequestType}</div>),
          data: displayRequestType,
        },
        {
          key: TABLE_COLUMN_KEYS.INITIATOR + index,
          content: withTooltip(initiator, <div className="text-overflow">{initiator}</div>),
          data: initiator,
        },
        {
          key: TABLE_COLUMN_KEYS.APPROVER + index,
          content: withTooltip(approver, <div className="text-overflow">{approver}</div>),
          data: approver,
        },
        {
          key: TABLE_COLUMN_KEYS.STATUS + index,
          content: getApprovalStatusContent(task.approvalStatus),
          data:
            task.approvalStatus && statusToShow[task.approvalStatus]
              ? statusToShow[task.approvalStatus].toUpperCase()
              : '',
        },
        {
          key: TABLE_COLUMN_KEYS.COMPLETED + index,
          content: withTooltip(formattedCompletedDate, <div className="text-overflow">{formattedCompletedDate}</div>),
          data: formattedCompletedDate,
        },
        {
          key: TABLE_COLUMN_KEYS.COMPLETION_TIME + index,
          content: withTooltip(completionTime, <div className="text-overflow">{completionTime}</div>),
          data: completionTime,
        },
      ],
    };
  });
};
